import { keccak256, toBytes } from "viem";
import type { Address, Hex } from "viem";
import { canonicalJson } from "./json";

/** The three pricing forms (D35). */
export type PriceRule =
  | { form: "fixed"; price: number }
  | {
      form: "tiered";
      measure: "number" | "length" | "codepoints" | "bytes";
      path: string;
      tiers: Array<[number, number]>; // [limit, price], limits rising; above the last limit the call is refused
    }
  | {
      form: "metered";
      meter: "openai-usage" | "apify-usage" | "bytes-tiers";
      per: number; // weighted units per 1 token
      maxPerCall: number;
      weights?: { prompt: number; cached: number; completion: number };
    };

export type ServiceKind = "runner" | "memory" | "model" | "tool" | "host" | "store";
export type BatchSize = number | "turn";

/** The price document a service serves at /pricing (CONTRACT.md 0.7). */
export interface PriceDocument {
  service: string;
  kinds: ServiceKind[];
  token: { symbol: string; address: Address | ""; decimals: 0 };
  payTo: Address | "";
  routes: Record<string, PriceRule>;
  defaultCap: number;
  batch: BatchSize;
  ready: boolean;
  version: number;
  models?: string[];
  tool?: string;
  labels?: string[];
  icon?: string;
  rate?: string; // informational: the chain's rate() is the authority
  /** A tool route's JSON-Schema parameter block (D34), offered to the model as the tool's definition. */
  toolSchema?: unknown;
  toolDescription?: string;
  /** How the Console summarises several routes: "metered · ≤ 5 tCloud per operation". */
  summary?: { mode: "fixed" | "tiered" | "metered"; max: number; unit: string };
}

export interface TokenWeights {
  prompt: number;
  cached: number;
  completion: number;
}
export const DEFAULT_WEIGHTS: TokenWeights = { prompt: 1, cached: 0.5, completion: 4 };

export function pricingHash(doc: PriceDocument): Hex {
  return keccak256(toBytes(canonicalJson(doc)));
}

export interface OpenAiUsage {
  prompt_tokens: number;
  completion_tokens: number;
  cached_tokens?: number;
}

/** weighted = (prompt − cached) × wp + cached × wc + completion × wo (D35; CONTRACT.md 0.7). */
export function weightedTokens(usage: OpenAiUsage, weights: TokenWeights = DEFAULT_WEIGHTS): number {
  const cached = Math.max(0, Math.min(usage.cached_tokens ?? 0, usage.prompt_tokens));
  const w =
    (usage.prompt_tokens - cached) * weights.prompt +
    cached * weights.cached +
    usage.completion_tokens * weights.completion;
  return Math.max(0, Math.round(w));
}

export class PricingError extends Error {
  constructor(
    public readonly code: "above_tiers" | "bad_measure" | "bad_rule",
    message?: string,
  ) {
    super(message ?? code);
  }
}

/** The actual of a call from its measures, in whole tokens: never 0, never above maxPerCall. */
export function priceOf(rule: PriceRule, measures: Record<string, number | string>): bigint {
  switch (rule.form) {
    case "fixed":
      return BigInt(Math.max(1, Math.floor(rule.price)));
    case "tiered": {
      const m = Number(measures.measure ?? measures.value ?? NaN);
      if (!Number.isFinite(m)) throw new PricingError("bad_measure");
      for (const [limit, price] of rule.tiers) {
        if (m <= limit) return BigInt(Math.max(1, Math.floor(price)));
      }
      throw new PricingError("above_tiers");
    }
    case "metered": {
      let weighted: number;
      if (rule.meter === "openai-usage") {
        weighted = weightedTokens(
          {
            prompt_tokens: Number(measures.prompt_tokens ?? 0),
            completion_tokens: Number(measures.completion_tokens ?? 0),
            cached_tokens: Number(measures.cached_tokens ?? 0),
          },
          rule.weights ?? DEFAULT_WEIGHTS,
        );
      } else if (rule.meter === "apify-usage") {
        weighted = Math.round(Number(measures.usd ?? 0) * 100); // cents
      } else {
        weighted = Number(measures.bytes ?? 0);
      }
      if (!Number.isFinite(weighted) || weighted < 0) throw new PricingError("bad_measure");
      const per = rule.per > 0 ? rule.per : 1;
      const actual = Math.max(1, Math.ceil(weighted / per));
      return BigInt(Math.min(actual, Math.max(1, Math.floor(rule.maxPerCall))));
    }
    default:
      throw new PricingError("bad_rule");
  }
}

/** The largest price a rule may charge for one call: the guard's worst case. */
export function maxPriceOf(rule: PriceRule): bigint {
  switch (rule.form) {
    case "fixed":
      return BigInt(Math.max(1, Math.floor(rule.price)));
    case "tiered":
      return BigInt(Math.max(1, ...rule.tiers.map(([, p]) => Math.floor(p))));
    case "metered":
      return BigInt(Math.max(1, Math.floor(rule.maxPerCall)));
  }
}

/** The measure of a tiered rule from the request body: a number at a path, a string's length, its code points, or the body's bytes. */
export function measureOf(rule: Extract<PriceRule, { form: "tiered" }>, body: string): number {
  if (rule.measure === "bytes") return new TextEncoder().encode(body).length;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    throw new PricingError("bad_measure", "the body is not JSON");
  }
  const value = valueAtPath(parsed, rule.path);
  if (rule.measure === "number") {
    const n = Number(value);
    if (!Number.isFinite(n)) throw new PricingError("bad_measure", `no number at ${rule.path}`);
    return n;
  }
  if (typeof value !== "string") throw new PricingError("bad_measure", `no string at ${rule.path}`);
  return rule.measure === "length" ? value.length : [...value].length;
}

/** `$.text`, `$.input.messages[0].content`: a small JSONPath subset. */
export function valueAtPath(obj: unknown, path: string): unknown {
  const parts = path
    .replace(/^\$\.?/, "")
    .split(/\.|\[|\]/)
    .filter(Boolean);
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** The pricing of the provider's services and the presets' defaults (D36, D63). */
export const PRESET_PRICING: Record<string, PriceRule> = {
  "openai-compatible": {
    form: "metered",
    meter: "openai-usage",
    per: 1000,
    maxPerCall: 100,
    weights: { ...DEFAULT_WEIGHTS },
  },
  "elevenlabs-tts": {
    form: "tiered",
    measure: "length",
    path: "$.text",
    tiers: [
      [500, 1],
      [2000, 3],
      [5000, 6],
    ],
  },
  "apify-run": { form: "metered", meter: "apify-usage", per: 1, maxPerCall: 20 },
  "jev-decide": {
    form: "tiered",
    measure: "bytes",
    path: "",
    tiers: [
      [8192, 1],
      [32768, 2],
      [65536, 4],
    ],
  },
  "generic-rest": { form: "fixed", price: 1 },
  memory: { form: "fixed", price: 1 },
  // a store (D34a, D74): 1 token per MiB started, a file of at most 8 MiB
  store: {
    form: "tiered",
    measure: "bytes",
    path: "",
    tiers: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => [n * 1024 * 1024, n] as [number, number]),
  },
  other: { form: "fixed", price: 1 },
};
